import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog, FCWithFragments } from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';

import {
  SetModuleMemberAsTrained_InterviewModuleMemberFragment,
  TrainingStatus,
  useModuleMemberUpdateMutation,
} from 'src/generated/mloop-graphql';

import { resetModuleMemberListCache } from 'src/entities/InterviewModuleMember/utils';

type Props = {
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
};

type Fragments = {
  interviewModuleMember: SetModuleMemberAsTrained_InterviewModuleMemberFragment;
};

const SetModuleMemberAsTrained: FCWithFragments<Fragments, Props> = ({
  interviewModuleMember,
  open,
  onClose,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [updateMember, { loading, error }] = useModuleMemberUpdateMutation();

  const handleUpdate = async () => {
    try {
      await updateMember({
        variables: {
          input: {
            employeeId: interviewModuleMember.employeeId,
            interviewModuleId: interviewModuleMember.interviewModuleId,
            status: TrainingStatus.Trained,
          },
        },
        refetchQueries: ['InterviewModuleMemberSidepanelQuery', 'InterviewModuleMemberModuleLogSidepanel'],
        update: (cache) => {
          resetModuleMemberListCache({
            cache,
            employeeIds: [interviewModuleMember.employeeId],
            moduleId: interviewModuleMember.interviewModuleId,
          });
        },
      });
      onUpdate();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Dialog
      cancelOptions={{ label: t('hiring-dashboard-common:common.cancel'), onClick: onClose }}
      submitOptions={{
        label: t('common:shared.actions.confirm'),
        onClick: handleUpdate,
        isDisabled: loading,
        endIcon: loading ? <CircularProgress size="16px" /> : undefined,
      }}
      title={t('hiring-dashboard-interviewers:interviewers.setAsTrained.title')}
      open={open}
      onClose={onClose}
    >
      <Stack direction="column" spacing={2}>
        <Typography>{t('hiring-dashboard-interviewers:interviewers.setAsTrained.body')}</Typography>
        {!!interviewModuleMember.stats?.upcoming && (
          <Alert variant="outlined" color="warning" severity="warning">
            <Typography>
              {t('hiring-dashboard-interviewers:interviewers.setAsTrained.upcomingTraineeWarning')}
            </Typography>
          </Alert>
        )}
      </Stack>

      <PublicError error={error} />
    </Dialog>
  );
};

SetModuleMemberAsTrained.fragments = {
  interviewModuleMember: gql`
    fragment SetModuleMemberAsTrained_interviewModuleMember on InterviewModuleMember {
      employeeId
      interviewModuleId
      stats {
        upcoming
      }
    }
  `,
};

export default SetModuleMemberAsTrained;
